import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertController, LoadingController, ModalController } from '@ionic/angular/standalone';
import { ComponentStore } from '@ngrx/component-store';
import { Dictionary } from '@ngrx/entity';
import { tapResponse } from '@ngrx/operators';
import moment from 'moment';
import { EMPTY, exhaustMap, map, Observable, tap, TimeoutError } from 'rxjs';
import { ItemChoicesComponent } from 'src/app/models/slip.model';
import { ServiceError, SlipService } from 'src/app/services/slip.service';
import { applyTimer } from 'src/app/services/timer';
import { ErrorLut, findError, PaymentState, ViewModel } from '../student-slip-modal.store';

export const componentSubmitErrorLut: ErrorLut<{ message: string; canRetry: boolean; showReload: boolean }> = {
  0: {
    message: `We weren't able to submit your response because it looks like you're offline. <br /><br />Please check your wifi or mobile data connection and try again later.`,
    canRetry: true,
    showReload: false
  },
  404: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    canRetry: false,
    showReload: false
  },
  2001: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    canRetry: false,
    showReload: false
  },
  2007: {
    message: `Response has already been submitted.<br /><br />Please tap 'Refresh' to update the slip.`,
    canRetry: false,
    showReload: true
  }
};

export const componentReservationErrorLut: ErrorLut<{ message: string; canRetry: boolean; showReload: boolean }> = {
  0: {
    message: `We weren't able to check reservations because it looks like you're offline. <br /><br />Please check your wifi or mobile data connection and try again later.`,
    canRetry: true,
    showReload: false
  },
  404: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    canRetry: false,
    showReload: false
  },
  2001: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    canRetry: false,
    showReload: false
  },
  2007: {
    message: `Response has already been submitted.<br /><br />Please tap 'Refresh' to update the slip.`,
    canRetry: false,
    showReload: true
  },
  2009: {
    message: `Item level restriction reached<br /><br />Please tap 'Refresh' to update the slip.`,
    canRetry: false,
    showReload: true
  }
};
export interface SlipComponentInfo {
  schoolId: number;
  schoolName: string;
  studentId: number;
  slipId: number;
  componentId: number;
  title: string;
}

export interface ItemChoice {
  itemId: number;
  choiceId: number;
  inventoryIds: number[];
  unitCost: number;
  taxRate: number;
  quantity: number;
  issue?: {
    legend: string;
    cap: number;
  };
}
export interface ItemModel {
  itemId: number;
  name: string;
  timeLegend: string;
  locationLegend: string;
  description: string;
  choiceLimitReached: boolean;
  choiceLimitLegend: string;
  choices: ChoiceModel[];
  itemAvailability: {
    legend: string;
    exhausted: boolean;
  };
}
export interface ChoiceModel {
  choiceId: number;
  choiceType: 'multiple' | 'single' | 'multiple-single-choice';
  headerTitle: string;
  headerTimeSlot: string;
  description: string;
  unitCost: number;
  taxRate: number;
  footerTitle: string;
  choiceLimit: number;
  inventoryIds: number[];

  remainingInventory: number;
  sharedAvailability: { limit: number; remaining: number };
  canAdd: boolean;
  inventoryLegend: string;
  currentCount: number;
  issueLegend: string;
}

export interface SummaryItem {
  id: number;
  name: string;
  itemTotalLegend: string;
  lineItems: {
    quantityLabel: string;
    timeLegend: string;
    nameLegend: string;
    costLegend: string;
  }[];
}
export interface SlipItemChoicesModalState {
  slipModalVM: ViewModel;
  state: 'choices' | 'issues' | 'summary' | 'view-summary';
  slipComponentInfo: SlipComponentInfo;
  component: ItemChoicesComponent;
  choicesMade: ItemChoice[];
  reservationExpiresOn: string;
  inventoryErrors: Dictionary<number>;
  inventoryUpdated: boolean;
}

@Injectable()
export class SlipItemChoicesModalStore extends ComponentStore<SlipItemChoicesModalState> {
  constructor(
    private slipService: SlipService,
    private loadingController: LoadingController,
    private modalController: ModalController,
    private alertController: AlertController
  ) {
    super({
      slipModalVM: null,
      state: 'choices',
      slipComponentInfo: null,
      component: null,
      choicesMade: [],
      reservationExpiresOn: null,
      inventoryErrors: {},
      inventoryUpdated: false
    });
  }
  readonly upsertItemChoice = this.updater((state, itemChoice: ItemChoice) => {
    const choicesMade = [...state.choicesMade].filter(cm => cm.choiceId !== itemChoice.choiceId);
    if (itemChoice.quantity > 0) choicesMade.push(itemChoice);

    return { ...state, choicesMade: choicesMade };
  });
  // Upsert an item choice and reset the quantity for other choices within this itemChoice item
  readonly upsertSingleItemChoice = this.updater((state, itemChoice: ItemChoice) => {
    const choicesMade = [...state.choicesMade].filter(cm => cm.itemId !== itemChoice.itemId);
    if (itemChoice.quantity > 0) choicesMade.push(itemChoice);

    return { ...state, choicesMade: choicesMade };
  });

  readonly applyInventoryLimits = this.updater((state, limits: Dictionary<number>) => {
    const modifiedChoices = [];
    state.component.payload.items.forEach((item) => {
      const itemInventory =
        item.inventory?.map((i) => {
          const serverRemaining = i.capacity - i.consumed;
          const locallyConsumed = state.choicesMade.reduce((accum, curr) => {
            return accum + (curr.inventoryIds?.includes(i.id) ? curr.quantity : 0);
          }, 0);
          return {
            inventoryId: i.id,
            remaining: serverRemaining - locallyConsumed,
            cap: limits[i.id] ?? null
          };
        }) ?? [];
      modifiedChoices.push(
        ...state.choicesMade
          .filter(c => c.itemId === item.id)
          .map((c) => {
            const choiceInventory =
              c.inventoryIds
                ?.map((id) => {
                  const inventoryItem = itemInventory.find(ii => ii.inventoryId === id);
                  return inventoryItem.cap ?? inventoryItem.remaining;
                })
                .filter(r => r >= 0) ?? [];
            const remaining = choiceInventory.length > 0 ? choiceInventory.reduce((a, b) => Math.min(a, b)) : null;
            if (remaining < c.quantity)
              c.issue = {
                cap: remaining,
                legend: remaining === 0 ? 'Removed' : 'Reduced from ' + c.quantity
              };
            return c;
          })
          .filter(c => c.quantity > 0)
      );
    });

    return {
      ...state,
      state: 'issues',
      choicesMade: modifiedChoices,
      inventoryErrors: limits,
      inventoryUpdated: true
    };
  });

  readonly applyInventoryErrors = this.updater((state) => {
    if (state.state !== 'issues') return state;

    const modifiedItems = [...state.component.payload.items].map(item => ({
      ...item,
      inventory: [...item.inventory].map(i => ({
        ...i,
        consumed: state.inventoryErrors[i.id] >= 0 ? i.capacity - state.inventoryErrors[i.id] : i.consumed
      }))
    }));
    const modifiedChoices = [...state.choicesMade].map(choice => ({
      ...choice,
      quantity: choice.issue?.cap ?? choice.quantity,
      issue: null
    }));

    return {
      ...state,
      component: { ...state.component, payload: { ...state.component.payload, items: modifiedItems } },
      choicesMade: modifiedChoices,
      inventoryErrors: {}
    };
  });

  readonly onBack = this.updater((state) => {
    return {
      ...state,
      state: 'choices'
    };
  });

  readonly headerModel$ = this.select((state) => {
    {
      return {
        buttonType: state.state === 'issues' || state.state === 'summary' ? 'back' : 'close'
      };
    }
  });
  readonly footerModel$ = this.select((state) => {
    {
      if (state.state === 'summary' || state.state === 'view-summary') return null;

      const modifiedChoices = [...state.choicesMade].map(choice => ({
        ...choice,
        quantity: choice.issue?.cap ?? choice.quantity,
        issue: null
      }));

      const stats = modifiedChoices.reduce(
        (p, n) => {
          return {
            count: p.count + n.quantity,
            cost: p.cost + n.unitCost * n.quantity
          };
        },
        { count: 0, cost: 0 }
      );
      const amount = (stats.cost / 100).toFixed(2);
      let legend = '';
      if (stats.count > 0) {
        legend += `${stats.count} item` + (stats.count > 1 ? 's' : '');
        if (stats.cost > 0) {
          legend += ` | £${amount}`;
        }
      }
      if (state.slipModalVM?.itemChoicesInfo?.balance < 0) {
        const credit = ((state.slipModalVM.itemChoicesInfo.balance / 100) * -1).toFixed(2);
        legend += ` | £${credit} credit`;
      }
      const canProceed = !!state.choicesMade.find(cm => cm.quantity > 0);
      return {
        canProceed: canProceed,
        summary: legend
      };
    }
  });

  readonly contentState$ = this.select(state => state.state);
  readonly itemsModel$ = this.select((state) => {
    {
      return state.component.payload.items.map((item) => {
        const itemInventory =
          item.inventory?.map((i) => {
            const serverRemaining = i.capacity ? i.capacity - i.consumed : null;
            return {
              type: i.type,
              inventoryId: i.id,
              serverRemaining: serverRemaining,
              remainingAfterChoices: serverRemaining
            };
          }) ?? [];
        const itemChoicesMade = state.choicesMade.reduce((a, b) => a + (b.itemId === item.id ? b.quantity : 0), 0);
        const itemChoiceLimitLegend = item.choiceLimit
          ? `${item.choiceLimit} ITEM LIMIT` + (itemChoicesMade === item.choiceLimit ? ' REACHED' : '')
          : null;
        const singleSelectionMode = item.choiceType === 'multiple-single-choice' || item.choiceLimit === 1;
        const checkboxMode = item.choiceLimit === 1;

        // Inventory can operate across choice e.g. an item limit so calculate inventory levels across choices
        const choicesMadeInventoryUsage = state.choicesMade
          .filter(cm => cm.itemId === item.id)
          .map(cm => ({ inventoryIds: cm.inventoryIds, quantity: cm.quantity }));
        itemInventory.forEach((ii) => {
          if (ii.serverRemaining !== null) {
            const choiceUsage = choicesMadeInventoryUsage.reduce(
              (sum, current) => (sum += current.inventoryIds.includes(ii.inventoryId) ? current.quantity : 0),
              0
            );
            ii.remainingAfterChoices -= choiceUsage;
          }
        });
        const itemAvailabilityInventory = itemInventory.find(ci => ci.type === 'item');
        const itemAvailability = itemAvailabilityInventory
          ? {
              legend: `${itemAvailabilityInventory.serverRemaining} AVAILABLE`,
              exhausted: itemAvailabilityInventory.remainingAfterChoices <= 0
            }
          : null;
        return {
          itemId: item.id,
          choiceType: item.choiceType,
          name: item.itemProperties.name ?? formatDate(item.itemProperties.date, 'EEE d MMM yy', 'en-gb'),
          timeLegend: this.generateTimeLegend(item.itemProperties.startTime, item.itemProperties.endTime),
          locationLegend: item.itemProperties.location ?? '',
          description: item.itemProperties.description,
          choiceLimitReached: itemChoicesMade >= item.choiceLimit,
          choiceLimitLegend: itemChoiceLimitLegend,
          singleSelectionMode: singleSelectionMode,
          checkboxMode: checkboxMode,
          itemAvailability: itemAvailability,
          choices: item.choices.map((c) => {
            const choiceInventory =
              c.inventoryIds
                ?.map(id => itemInventory.find(ii => ii.inventoryId === id))
                .filter(r => r.serverRemaining === 0 || r.serverRemaining > 0) ?? [];
            const serverRemaining =
              choiceInventory.length > 0 ? Math.min(...choiceInventory.map(ci => ci.serverRemaining)) : null;
            const remainingAfterChoices =
              choiceInventory.length > 0 ? Math.min(...choiceInventory.map(ci => ci.remainingAfterChoices)) : null;
            let inventoryLegend = '';
            if (serverRemaining !== null) {
              if (serverRemaining === 0) inventoryLegend = 'SOLD OUT';
              if (serverRemaining > 0) inventoryLegend = `${serverRemaining} left`;
            }

            const costLegend = c.pricing
              ? c.pricing.default === 0
                ? 'FREE'
                : '£' + (c.pricing.default / 100).toFixed(2)
              : '';

            let headerTitle = c.name;
            let footerTitle = costLegend;
            const headerTimeslot = c.startTime ? this.generateTimeLegend(c.startTime, c.endTime) : null;
            if (!c.name && !headerTimeslot && inventoryLegend) {
              headerTitle = costLegend;
              footerTitle = '';
            }
            const itemChoiceLimitReached = !!item.choiceLimit && itemChoicesMade >= item.choiceLimit;
            const currentQuantity = state.choicesMade.find(cm => cm.choiceId === c.id)?.quantity ?? 0;
            const inventoryExhausted = !!item.inventory && remainingAfterChoices !== null && remainingAfterChoices <= 0;

            const lowestSharedInventory = choiceInventory
              .filter(ci => ['manual', 'item'].includes(ci.type))
              .reduce((prev, curr) => (prev?.remainingAfterChoices < curr.remainingAfterChoices ? prev : curr), null);
            const sharedAvailability = lowestSharedInventory
              ? { limit: lowestSharedInventory.serverRemaining, remaining: lowestSharedInventory.remainingAfterChoices }
              : null;
            return {
              choiceId: c.id,
              headerTitle: headerTitle,
              headerTimeSlot: headerTimeslot,
              description: c.description,
              unitCost: c.pricing?.default,
              taxRate: c.pricing?.taxInfo?.taxRate,
              footerTitle: footerTitle,
              choiceLimit: c.choiceLimit,
              sharedAvailability: sharedAvailability,
              inventoryIds: c.inventoryIds,
              remainingInventory: item.inventory ? serverRemaining : null,
              canAdd: checkboxMode ? true : !(itemChoiceLimitReached || inventoryExhausted),
              inventoryLegend: inventoryLegend,
              currentCount: currentQuantity
            } as ChoiceModel;
          })
        };
      });
    }
  }).pipe(tap(console.log));

  readonly summaryModel$ = this.state$.pipe(
    map((state) => {
      // Choices are based on already submitted data or about to be submitted
      const choicesMade = state.slipModalVM?.itemChoicesInfo.choices ?? state.choicesMade;

      const due = choicesMade.reduce((accum, item) => {
        return accum + (item.unitCost ?? 0) * item.quantity;
      }, 0);

      const itemData: SummaryItem[] = [];
      state.component.payload.items.forEach((i) => {
        const itemChoices = choicesMade.filter(cm => cm.quantity > 0 && cm.itemId === i.id);
        const checkboxMode = i.choiceLimit === 1;
        if (itemChoices.length > 0) {
          const choiceLineItems = [];
          i.choices.forEach((c) => {
            const itemChoice = itemChoices.find(ic => ic.choiceId === c.id);
            if (itemChoice) {
              const quantityLabel = checkboxMode ? null : `${itemChoice.quantity}x`;
              const timeLegend = this.generateTimeLegend(i.itemProperties.startTime, i.itemProperties.endTime);
              const formattedCost = itemChoice.unitCost ? (itemChoice.unitCost / 100.0).toFixed(2) : null;
              choiceLineItems.push({
                quantityLabel: quantityLabel,
                timeLegend: timeLegend,
                nameLegend: c.name,
                costLegend: formattedCost
              });
            }
          });
          const name = i.itemProperties.name ?? formatDate(i.itemProperties.date, 'EEE d MMM yy', 'en-gb');
          const itemTotal = itemChoices.reduce((accum, curr) => {
            return accum + (curr.unitCost ? curr.quantity * curr.unitCost : 0);
          }, 0);
          const itemTotalLegend = itemTotal > 0 ? '£' + (itemTotal / 100.0).toFixed(2) : '';
          itemData.push({
            id: i.id,
            name: name,
            itemTotalLegend: itemTotalLegend,
            lineItems: choiceLineItems
          });
        }
      });

      // A negative balance implies we have some credit to apply
      // e.g. After a response has been cleared and not completely refunded
      const toPay =
        state.slipModalVM.itemChoicesInfo.balance < 0
          ? due + state.slipModalVM.itemChoicesInfo.balance
          : state.slipModalVM.itemChoicesInfo.balance ?? due;

      const componentInfo = {
        ...state.slipModalVM.itemChoicesInfo,
        due: due,
        balance: toPay
      };

      return {
        requiresPayment: toPay > 0 || state.slipModalVM?.paymentProgress.state === PaymentState.PaymentConfirmed,
        itemData: itemData,
        itemChoicesComponentInfo: componentInfo,
        paymentProgress: state.slipModalVM?.paymentProgress,
        reservationExpiresOn: state.reservationExpiresOn
      };
    })
  );
  readonly issuesModel$ = this.select((state) => {
    {
      return state.component.payload.items
        .map((item) => {
          const itemChoiceLimitLegend = item.choiceLimit ? `${item.choiceLimit} ITEM LIMIT` : null;
          return {
            itemId: item.id,
            choiceType: item.choiceType,
            name: item.itemProperties.name ?? formatDate(item.itemProperties.date, 'EEE d MMM yy', 'en-gb'),
            timeLegend: this.generateTimeLegend(item.itemProperties.startTime, item.itemProperties.endTime),
            locationLegend: item.itemProperties.location ?? '',
            description: item.itemProperties.description,
            choiceLimitLegend: itemChoiceLimitLegend,
            choices: item.choices
              .map((c) => {
                const costLegend = c.pricing
                  ? c.pricing.default === 0
                    ? 'FREE'
                    : '£' + (c.pricing.default / 100).toFixed(2)
                  : '';

                const choiceMade = state.choicesMade.find(cm => cm.choiceId === c.id);
                if (!choiceMade?.issue) return null;
                const inventoryLegend = choiceMade.issue.cap > 0 ? `Only ${choiceMade.issue.cap} left` : `0 left`;
                const issueLegend = choiceMade.issue.legend;
                let headerTitle = c.name;
                let footerTitle = costLegend;
                const headerTimeslot = c.startTime ? this.generateTimeLegend(c.startTime, c.endTime) : null;
                if (!c.name && !headerTimeslot && inventoryLegend) {
                  headerTitle = costLegend;
                  footerTitle = '';
                }
                return {
                  choiceId: c.id,
                  headerTitle: headerTitle,
                  headerTimeSlot: headerTimeslot,
                  description: c.description,
                  unitCost: c.pricing?.default,
                  footerTitle: footerTitle,
                  inventoryIds: c.inventoryIds,
                  remainingInventory: choiceMade.issue.cap,
                  inventoryLegend: inventoryLegend,
                  currentCount: choiceMade.issue.cap,
                  issueLegend: issueLegend
                } as ChoiceModel;
              })
              .filter(c => !!c)
          };
        })
        .filter(i => i.choices.length > 0);
    }
  });
  readonly showLoadingMessage = this.effect((params$: Observable<{ message: string }>) => {
    return params$.pipe(
      exhaustMap(async (params) => {
        const loading = await this.loadingController.create({
          message: params.message
        });
        await loading.present();
      })
    );
  });
  readonly hideLoadingMessage = this.effect((trigger$) => {
    return trigger$.pipe(
      exhaustMap(async () => {
        await this.loadingController.dismiss();
      })
    );
  });

  readonly showAlert = this.effect(
    (
      params$: Observable<{
        messageDetail: {
          message: string;
          canRetry: boolean;
          showReload: boolean;
        };
        handler?: (value: any) => any;
      }>
    ) => {
      return params$.pipe(
        exhaustMap(async (params) => {
          const alert = await this.alertController.create({
            message: params.messageDetail.message,
            buttons: params.messageDetail.canRetry
              ? [
                  {
                    text: 'Cancel',
                    role: 'cancel'
                  },
                  {
                    text: 'Retry',
                    handler: params.handler
                  }
                ]
              : params.messageDetail.showReload
                ? [
                    {
                      text: 'Refresh',
                      handler: () => {
                        this.modalController.dismiss({ reloadSlip: true });
                      }
                    }
                  ]
                : ['Ok']
          });
          await alert.present();
        })
      );
    }
  );

  readonly showReservationRefreshRequired = this.effect((trigger$) => {
    return trigger$.pipe(
      exhaustMap(async () => {
        const alert = await this.alertController.create({
          message: 'Availability expired, press refresh to update',
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel'
            },
            {
              text: 'Refresh',
              handler: () => {
                this.checkReservations();
              }
            }
          ]
        });
        await alert.present();
      })
    );
  });

  readonly checkReservations = this.effect((trigger$) => {
    return trigger$.pipe(
      exhaustMap(() => {
        this.applyInventoryErrors();
        this.showLoadingMessage({ message: 'Checking availability...' });
        const componentInfo = this.get().slipComponentInfo;
        const itemChoices = this.get().choicesMade.map(c => ({
          itemId: c.itemId,
          choiceId: c.choiceId,
          unitCost: c.unitCost,
          taxRate: c.taxRate,
          quantity: c.quantity
        }));
        const apiCall = this.slipService.createChoiceItemsComponentReservation(
          componentInfo.schoolId,
          componentInfo.studentId,
          componentInfo.slipId,
          componentInfo.componentId,
          { itemChoices: itemChoices }
        );
        return applyTimer(apiCall).pipe(
          tapResponse(
            (result) => {
              this.hideLoadingMessage();
              this.patchState({ state: 'summary', reservationExpiresOn: result.expiresOn });
            },
            (errorResponse: HttpErrorResponse | TimeoutError) => {
              const error = errorResponse instanceof HttpErrorResponse ? errorResponse.error : null;
              const statusCode = errorResponse instanceof HttpErrorResponse ? errorResponse.status : -1;
              this.hideLoadingMessage();
              if (error?.ErrorCode === 2008) {
                this.applyInventoryLimits(error.ErrorInfo);
              } else {
                const messageDetail = findError(error?.ErrorCode ?? statusCode, componentReservationErrorLut, {
                  message: `We weren't able to check reservations. <br /><br />Please try again later.`,
                  canRetry: false,
                  showReload: false
                });

                this.showAlert({ messageDetail: messageDetail, handler: this.checkReservations });
              }
            }
          )
        );
      })
    );
  });

  readonly confirmReservations = this.effect((trigger$) => {
    return trigger$.pipe(
      exhaustMap(() => {
        const expired = moment().isAfter(moment(this.get().reservationExpiresOn));
        if (expired) {
          this.showReservationRefreshRequired();
          return EMPTY;
        }

        this.showLoadingMessage({ message: 'Confirming choices...' });
        const componentInfo = this.get().slipComponentInfo;
        const itemChoices = this.get().choicesMade.map(c => ({
          itemId: c.itemId,
          choiceId: c.choiceId,
          unitCost: c.unitCost,
          taxRate: c.taxRate,
          quantity: c.quantity
        }));
        const apiCall = this.slipService.submitComponentResponse(
          componentInfo.schoolId,
          componentInfo.studentId,
          componentInfo.slipId,
          componentInfo.componentId,
          { itemChoices: itemChoices }
        );

        return applyTimer(apiCall).pipe(
          tapResponse(
            (result) => {
              this.hideLoadingMessage();
              this.modalController.dismiss({ slip: result });
            },
            async (error: ServiceError) => {
              this.hideLoadingMessage();
              // this.patchState({ state: 'issues' });
              if (error.stage === 0) {
                const messageDetail = findError(
                  error.apiError?.ErrorCode ?? error.httpStatus,
                  componentSubmitErrorLut,
                  {
                    message: `We weren't able to submit your response. <br /><br />Please try again later.`,
                    canRetry: false,
                    showReload: false
                  }
                );

                this.showAlert({ messageDetail: messageDetail, handler: this.confirmReservations });
              } else {
                // Submit success but slip load failed so tell caller that
                // we need to reload slip page
                this.modalController.dismiss({ reloadSlip: true });
              }
            }
          )
        );
      })
    );
  });

  readonly dismissModal = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(() => {
        this.modalController.dismiss({
          itemUpdates: this.get().inventoryUpdated ? this.get().component.payload.items : null
        });
      })
    );
  });

  private generateTimeLegend(startTime: string, endTime: string): string {
    if (!startTime) return '';
    return startTime + (endTime ? ' - ' + endTime : '');
  }
}
